import React, { useState } from 'react';

import styles from './styles.module.css';
import deleteIcon from '../../assets/icons/errorToast.svg';
import informationIcon from '../../assets/icons/information.svg';
import okIcon from '../../assets/icons/ok.svg';
import warningIcon from '../../assets/icons/warning.svg';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastProps {
  id: string;
  message: string;
  type?: ToastType;
  autoClose?: number;
  closeOnClick?: boolean;
  icon?: string | React.ComponentType;
  actionText?: string;
  onActionClick?: () => void;
  onClose: (id: string) => void;
  className?: string;
  filled?: boolean;
  technicalMessage?: string;
  onSeeDetailsClick?: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  message,
  type = 'info',
  closeOnClick = true,
  icon,
  actionText,
  onActionClick,
  onClose,
  className = '',
  filled = true,
  technicalMessage,
  onSeeDetailsClick
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const hasTechnicalMessage = Boolean(
    technicalMessage && technicalMessage.trim() && onSeeDetailsClick
  );

  const getToastTypeClass = (toastType: ToastType): string => {
    switch (toastType) {
      case 'info':
        return styles.toastInfo;
      case 'success':
        return styles.toastSuccess;
      case 'warning':
        return styles.toastWarning;
      case 'error':
        return styles.toastError;
      default:
        return styles.toastInfo;
    }
  };

  const getDefaultIcon = (toastType: ToastType): string => {
    switch (toastType) {
      case 'success':
        return okIcon;
      case 'warning':
        return warningIcon;
      case 'info':
        return informationIcon;
      case 'error':
        return deleteIcon;
      default:
        return informationIcon;
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose(id);
    }, 300);
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClose();
  };

  const handleSeeDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSeeDetailsClick?.();
  };

  return (
    <div
      className={`${styles.toast} ${getToastTypeClass(type)} ${isClosing ? styles.toastClosing : ''} ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className={styles.toastMainRow}>
        <div className={styles.toastSide} />

        <div className={styles.toastContent}>
          <div className={filled ? styles.toastIcon : styles.toastIconNoFilled}>
            {icon ? (
              typeof icon === 'string' ? (
                <img src={icon} alt="Toast icon" />
              ) : (
                React.createElement(icon)
              )
            ) : (
              <img src={getDefaultIcon(type)} alt="Toast icon" />
            )}
          </div>
          <div className={styles.toastMessageContainer}>
            <span className={styles.toastMessage}>{message}</span>
            {hasTechnicalMessage && (
              <>
                <span className={styles.toastSeparator} aria-hidden="true">
                  ·
                </span>
                <button
                  id={`toastSeeDetailsButton-${id}`}
                  className={styles.seeDetailsButton}
                  onClick={handleSeeDetailsClick}
                  type="button"
                >
                  See details
                </button>
              </>
            )}
            {actionText && onActionClick && (
              <button
                className={styles.actionButton}
                onClick={e => {
                  e.stopPropagation();
                  onActionClick();
                }}
                type="button"
              >
                {actionText}
              </button>
            )}
          </div>
        </div>

        <div className={styles.toastSide}>
          {closeOnClick && (
            <button
              id={`toastCloseButton-${id}`}
              className={styles.closeButton}
              onClick={handleCloseClick}
              aria-label="Close notification"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Toast;
